import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Eye, EyeOff, Check, X } from "lucide-react";
import { AuthenticationService } from "@/api/auth";
import Logo from "@/assets/img/eventLogo1.svg";

// Password requirements
const passwordRequirements = [
  { id: 1, text: "Minimum 8 characters", regex: /.{8,}/ },
  { id: 2, text: "At least one uppercase letter", regex: /[A-Z]/ },
  { id: 3, text: "At least one lowercase letter", regex: /[a-z]/ },
  { id: 4, text: "At least one number", regex: /[0-9]/ },
  {
    id: 5,
    text: "At least one special character",
    regex: /[!@#$%^&*(),.?":{}|<>]/,
  },
];

const ChangePassword = () => {
  const [searchParams] = useSearchParams();
  const { postAuthResetPassword } = AuthenticationService;
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [requirementsMet, setRequirementsMet] = useState<{
    [key: number]: boolean;
  }>({});
  const navigate = useNavigate();
  const { toast } = useToast();

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  // Validate password against requirements
  useEffect(() => {
    const errors: string[] = [];
    const met: { [key: number]: boolean } = {};

    passwordRequirements.forEach((req) => {
      const isValid = req.regex.test(newPassword);
      met[req.id] = isValid;
      if (!isValid && newPassword) {
        errors.push(req.text);
      }
    });

    setRequirementsMet(met);
    setPasswordErrors(errors);
  }, [newPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!token || !email) {
      toast({
        variant: "destructive",
        title: "Invalid link",
        description: "The password reset link is invalid or expired.",
      });
      setIsLoading(false);
      return;
    }

    if (passwordErrors.length > 0) {
      toast({
        variant: "destructive",
        title: "Password doesn't meet requirements",
        description: "Please ensure your password meets all the criteria.",
      });
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
      });
      setIsLoading(false);
      return;
    }

    try {
      const response: any = await postAuthResetPassword({
        session: token,
        email,
        new_password: newPassword,
      });

      if (response.statusCode === 200) {
        toast({
          title: "Password changed",
          description: "Your password has been updated successfully.",
        });
        navigate("/signin");
      } else {
        toast({
          variant: "destructive",
          title: "Error changing password",
          description: response.body.message || "Please try again.",
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error changing password",
        description: "There was a problem updating your password.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const metCount = Object.values(requirementsMet).filter(Boolean).length;
    const total = passwordRequirements.length;
    const percentage = (metCount / total) * 100;

    if (percentage < 40) return "Weak";
    if (percentage < 80) return "Moderate";
    return "Strong";
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="flex items-center mb-4">
        <img
          src={Logo}
          alt="CecureStreams Logo"
          className="h-full w-[200px] text-cecure-700"
        />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            Change Password
          </CardTitle>
          <CardDescription className="text-center">
            {email ? `Updating password for ${email}` : "Set your new password"}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute top-1/2 right-3 transform -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <Eye className="h-5 w-5 text-cecure-700" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-cecure-700" />
                  )}
                </button>
              </div>

              {/* Password strength indicator */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">
                      Password strength:
                    </span>
                    <span
                      className={`text-sm font-semibold ${
                        getPasswordStrength() === "Weak"
                          ? "text-red-500"
                          : getPasswordStrength() === "Moderate"
                          ? "text-yellow-500"
                          : "text-green-500"
                      }`}
                    >
                      {getPasswordStrength()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        getPasswordStrength() === "Weak"
                          ? "bg-red-500"
                          : getPasswordStrength() === "Moderate"
                          ? "bg-yellow-500"
                          : "bg-green-500"
                      }`}
                      style={{
                        width: `${
                          (Object.values(requirementsMet).filter(Boolean)
                            .length /
                            passwordRequirements.length) *
                          100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Password requirements */}
              <div className="mt-3 space-y-1">
                <p className="text-sm font-medium">Password requirements:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  {passwordRequirements.map((req) => (
                    <li key={req.id} className="flex items-center">
                      {requirementsMet[req.id] ? (
                        <Check className="h-4 w-4 text-green-500 mr-2" />
                      ) : (
                        <X className="h-4 w-4 text-red-500 mr-2" />
                      )}
                      <span
                        className={
                          requirementsMet[req.id] ? "text-green-600" : ""
                        }
                      >
                        {req.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute top-1/2 right-3 transform -translate-y-1/2"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <Eye className="h-5 w-5 text-cecure-700" />
                  ) : (
                    <EyeOff className="h-5 w-5 text-cecure-700" />
                  )}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-sm text-red-500">Passwords do not match</p>
              )}
            </div>
          </CardContent>
          <CardContent>
            <Button
              type="submit"
              className="w-full bg-cecure-700 hover:bg-cecure-800"
              disabled={
                isLoading ||
                passwordErrors.length > 0 ||
                newPassword !== confirmPassword
              }
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Password"
              )}
            </Button>
          </CardContent>
        </form>
        <CardContent className="text-center text-sm">
          Remember your password?{" "}
          <Link
            to="/signin"
            className="text-cecure-700 hover:text-cecure-900 font-medium"
          >
            Sign In
          </Link>
        </CardContent>
      </Card>
    </div>
  );
};

export default ChangePassword;
